// ═══════════════════════════════════════════════════════════════════════════
// PAGE: MES QUOTAS (mai 2026, mode US)
// Affiche les crédits IA de l'utilisateur courant : solde restant (porte-monnaie
// qui pilote le blocage) + consommation par fonction (Analyse / Génération CR).
// Accessible à tout user.
// ═══════════════════════════════════════════════════════════════════════════

interface QuotaResponse {
  period_start: string;
  month_start_day: number;
  tenant_type: string;
  credits_per_user_monthly: number;
  credits_remaining: number;
  credits_monthly_remaining: number;
  credits_extra: number;
  used: { analyze: number; redaction: number; review: number };
}

function _formatDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function _nextPeriodStart(periodStart: string): string {
  try {
    const d = new Date(periodStart + 'T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + 1);
    return _formatDate(d.toISOString().slice(0, 10));
  } catch {
    return '';
  }
}

function _walletCard(remaining: number, consumed: number, monthly: number, extra: number): string {
  const total = consumed + remaining;
  const pct = total > 0 ? Math.min(100, Math.round((consumed / total) * 100)) : 0;
  const color = pct >= 90 ? '#c0392b' : pct >= 70 ? '#d68910' : '#147a57';
  const breakdown =
    extra > 0
      ? `<p class="quota-breakdown">Dont ${monthly} crédit${monthly > 1 ? 's' : ''} du quota mensuel et ${extra} crédit${extra > 1 ? 's' : ''} acheté${extra > 1 ? 's' : ''}.</p>`
      : '';
  return `
    <div class="quota-card">
      <h3>Crédits IA restants</h3>
      <div class="quota-numbers">
        <div class="quota-used"><strong>${remaining}</strong> restant${remaining > 1 ? 's' : ''}</div>
        <div class="quota-total">sur ${total} ce mois</div>
        <div class="quota-remaining">${consumed} consommé${consumed > 1 ? 's' : ''}</div>
      </div>
      <div class="quota-bar"
           role="progressbar"
           aria-valuenow="${consumed}"
           aria-valuemin="0"
           aria-valuemax="${total}">
        <div class="quota-bar-fill" style="width:${pct}%;background:${color};"></div>
      </div>
      ${breakdown}
    </div>
  `;
}

// Une ligne par fonction consommatrice de crédits. `review` (revue du
// référentiel, août 2026) manquait : ses crédits étaient bien décomptés du
// solde, mais aucune ligne ne disait où ils étaient passés — l'écran affichait
// donc une consommation inférieure au débit constaté.
export const USAGE_ROWS: ReadonlyArray<{
  key: 'analyze' | 'redaction' | 'review';
  label: string;
  tone: string;
}> = [
  { key: 'analyze', label: 'Analyse CR (IA)', tone: '#3B82F6' },
  { key: 'redaction', label: 'Génération CR (IA)', tone: '#10B981' },
  { key: 'review', label: 'Revue du référentiel (IA)', tone: '#8B5CF6' },
];

function _usageCard(used: QuotaResponse['used']): string {
  const rows = USAGE_ROWS.map(
    (row) => `
      <div class="quota-usage-row">
        <span class="quota-usage-dot" style="background:${row.tone}"></span>
        <span>${esc(row.label)}</span>
        <strong class="quota-usage-val">${used[row.key] || 0}</strong>
      </div>`,
  ).join('');
  return `
    <div class="quota-card">
      <h3>Consommation par fonction</h3>
      ${rows}
    </div>
  `;
}

pages.myQuotas = async function () {
  setHeader('Mes quotas', '');
  setContent('<div class="loading">Chargement…</div>');

  let data: QuotaResponse;
  try {
    data = (await api.myQuotas()) as QuotaResponse;
  } catch (e) {
    setContent(
      `<div class="error">Impossible de charger vos crédits. ${esc(String((e as Error)?.message || ''))}</div>`,
    );
    return;
  }

  const remaining = data.credits_remaining || 0;
  const monthly = data.credits_monthly_remaining || 0;
  const extra = data.credits_extra || 0;
  // Le total suit les MÊMES lignes que la carte : deux additions distinctes
  // finiraient par diverger, et l'écran se contredirait.
  const consumed = USAGE_ROWS.reduce((n, row) => n + (data.used[row.key] || 0), 0);
  const tenantLabel = data.tenant_type === 'solo' ? 'Compte solo' : 'Compte équipe';
  const purchaseBtn =
    data.tenant_type === 'solo'
      ? `<button type="button" class="btn btn-secondary" id="btn-buy-credits" disabled
                title="À venir">Acheter des crédits</button>`
      : '';

  setContent(`
    <section class="my-quotas">
      <div class="my-quotas-header">
        <span class="badge">${esc(tenantLabel)}</span>
        <span class="my-quotas-period">
          Période en cours : démarre le ${esc(_formatDate(data.period_start))} ·
          réinitialisation prévue le ${esc(_nextPeriodStart(data.period_start))}
        </span>
      </div>

      <div class="my-quotas-grid">
        ${_walletCard(remaining, consumed, monthly, extra)}
        ${_usageCard(data.used)}
      </div>

      <div class="my-quotas-actions">
        ${purchaseBtn}
      </div>

      <p class="my-quotas-note">
        Vos crédits IA sont décomptés à chaque appel aux fonctions Analyse et
        Génération de comptes-rendus. ${
          data.tenant_type === 'solo'
            ? "Une fois votre solde épuisé, les appels IA sont bloqués jusqu'à la réinitialisation mensuelle ou l'ajout de crédits."
            : "Le solde de crédits est partagé par l'ensemble du tenant."
        }
      </p>
    </section>
  `);
};

export {};
